import React, { useEffect, useState, useCallback } from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, Skeleton } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useAdminFetch } from '../../hooks/useAdminFetch';

interface RealtimeData {
  active_visitors: number;
  active_pages: Array<{
    path: string;
    count: number;
  }>;
}

const RealtimeWidget: React.FC = () => {
  const [data, setData] = useState<RealtimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchRealtime = useCallback(async () => {
    try {
      const result = await adminFetch(`${apiUrl}/api/admin/analytics/realtime/`);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch realtime data:', err);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, apiUrl]);

  useEffect(() => {
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchRealtime]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FiberManualRecordIcon
            sx={{
              color: 'success.main',
              fontSize: 12,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
          <Typography variant="subtitle2" color="text.secondary">
            ACTIVE NOW
          </Typography>
        </Box>

        {loading ? (
          <Skeleton width={60} height={48} />
        ) : (
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {data?.active_visitors || 0}
          </Typography>
        )}

        {data?.active_pages && data.active_pages.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Current pages
            </Typography>
            <List dense disablePadding>
              {data.active_pages.slice(0, 5).map((page, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {page.path}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {page.count}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RealtimeWidget;
